import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Swords, Users, RotateCcw, Crown, Printer, Shuffle, ListOrdered } from 'lucide-react';
import { Card, Button, Badge } from '../ui';
import { cn } from '../../lib/utils';
import { useNodeRoster } from '../../hooks/useNodeRoster';
import { NodeRosterPickerModal } from './common/NodeRosterPickerModal';
import { DirectoryMember } from '../../types';
import toast from 'react-hot-toast';

interface Match {
  id: string;
  teamA: string | null;
  teamB: string | null;
  winner: string | null;
}

type Round = Match[];

// Mathematical tournament seed generator: 1 vs 8, 4 vs 5, 2 vs 7, 3 vs 6
const getSeededPairs = (n: number): number[] => {
  if (n <= 2) return [1, 2];
  const rounds = Math.log2(n) - 1;
  let pls = [1, 2];
  for (let i = 0; i < rounds; i++) {
    const next: number[] = [];
    for (const d of pls) {
      next.push(d);
      next.push(2 * pls.length + 1 - d);
    }
    pls = next;
  }
  return pls;
};

const BRACKET_PRESETS: Record<string, string[]> = {
  '4 Teams': [
    '#1 Red Dragons',
    '#2 Blue Sharks',
    '#3 Golden Eagles',
    '#4 Silver Wolves'
  ],
  '8 Teams': [
    '#1 Alpha Predators',
    '#2 Beta Storm',
    '#3 Gamma Rays',
    '#4 Delta Force',
    '#5 Epsilon Titans',
    '#6 Zeta Thunder',
    '#7 Eta Blazers',
    '#8 Theta Vipers'
  ],
  '16 Teams': [
    '#1 Apex Legends', '#2 Thunderbirds', '#3 Iron Giants', '#4 Red Hawks',
    '#5 Night Owls', '#6 Cobras', '#7 Silverbacks', '#8 White Rhinos',
    '#9 Arctic Foxes', '#10 Sea Otters', '#11 Grizzly Bears', '#12 Timber Wolves',
    '#13 Wildcats', '#14 Wolverines', '#15 Falcons', '#16 Black Panthers'
  ]
};

export function TournamentBracketGenerator() {
  const [inputText, setInputText] = useState('');
  const [rounds, setRounds] = useState<Round[]>([]);
  const [champion, setChampion] = useState<string | null>(null);
  const [gameState, setGameState] = useState<'setup' | 'playing' | 'finished'>('setup');
  const [seedMode, setSeedMode] = useState<'seeded' | 'random'>('seeded');

  const { hasActiveNode, nodeName } = useNodeRoster();
  const [isRosterModalOpen, setIsRosterModalOpen] = useState(false);

  const handleImportFromNode = (selectedMembers: DirectoryMember[]) => {
    if (selectedMembers.length === 0) return;
    const names = selectedMembers.map(m => m.name).filter(Boolean);
    const existing = inputText.trim() ? inputText.trim() + '\n' : '';
    setInputText(existing + names.join('\n'));
    toast.success(`Imported ${names.length} competitors from ${nodeName || 'organization'}`);
  };

  const generateBracket = () => {
    const teams = inputText.split('\n').map(t => t.trim()).filter(t => t.length > 0);
    if (teams.length < 2) {
      alert("Need at least 2 teams.");
      return;
    }

    // Find nearest power of 2
    const power = Math.ceil(Math.log2(teams.length));
    const targetSize = Math.pow(2, power);
    
    // Pad with BYEs
    while (teams.length < targetSize) {
      teams.push("BYE");
    }

    // Shuffle or Seed
    let orderedTeams: string[] = [];
    if (seedMode === 'seeded') {
      const pairOrder = getSeededPairs(targetSize);
      orderedTeams = pairOrder.map(seedNum => teams[seedNum - 1] || 'BYE');
    } else {
      orderedTeams = teams.sort(() => Math.random() - 0.5);
    }

    const initialMatches: Match[] = [];
    for (let i = 0; i < orderedTeams.length; i += 2) {
      const teamA = orderedTeams[i];
      const teamB = orderedTeams[i + 1];
      // Auto-advance if playing a BYE
      let winner = null;
      if (teamA === "BYE" && teamB !== "BYE") winner = teamB;
      if (teamB === "BYE" && teamA !== "BYE") winner = teamA;

      initialMatches.push({
        id: `r0-m${i/2}`,
        teamA,
        teamB,
        winner
      });
    }

    const newRounds: Round[] = [initialMatches];

    // Build empty subsequent rounds
    let currentRoundSize = initialMatches.length;
    let roundIndex = 1;
    while (currentRoundSize > 1) {
      currentRoundSize = currentRoundSize / 2;
      const nextRound: Match[] = [];
      for (let i = 0; i < currentRoundSize; i++) {
        nextRound.push({
          id: `r${roundIndex}-m${i}`,
          teamA: null,
          teamB: null,
          winner: null
        });
      }
      newRounds.push(nextRound);
      roundIndex++;
    }

    setRounds(newRounds);
    setChampion(null);
    setGameState('playing');
    
    // Process initial BYEs
    processByes(newRounds);
  };

  const processByes = (currentRounds: Round[]) => {
    const updated = [...currentRounds];
    // If any match in round 0 has a winner (due to BYE), push it to round 1
    updated[0].forEach((match, index) => {
      if (match.winner) {
        advanceTeam(0, index, match.winner, updated);
      }
    });
    setRounds(updated);
  };

  const advanceTeam = (roundIndex: number, matchIndex: number, team: string, roundsRef?: Round[]) => {
    const targetRounds = roundsRef || [...rounds];
    
    // Set winner of current match
    targetRounds[roundIndex][matchIndex].winner = team;

    // Is this the final match?
    if (roundIndex === targetRounds.length - 1) {
      setChampion(team);
      setGameState('finished');
      if (!roundsRef) setRounds(targetRounds);
      return;
    }

    // Determine next match slot
    const nextRoundIndex = roundIndex + 1;
    const nextMatchIndex = Math.floor(matchIndex / 2);
    const isTeamA = matchIndex % 2 === 0;

    if (isTeamA) {
      targetRounds[nextRoundIndex][nextMatchIndex].teamA = team;
    } else {
      targetRounds[nextRoundIndex][nextMatchIndex].teamB = team;
    }

    // Check if next match is against a BYE
    const nextMatch = targetRounds[nextRoundIndex][nextMatchIndex];
    if (nextMatch.teamA === "BYE" && nextMatch.teamB) {
      advanceTeam(nextRoundIndex, nextMatchIndex, nextMatch.teamB, targetRounds);
    } else if (nextMatch.teamB === "BYE" && nextMatch.teamA) {
      advanceTeam(nextRoundIndex, nextMatchIndex, nextMatch.teamA, targetRounds);
    }

    if (!roundsRef) {
      setRounds(targetRounds);
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto p-4 space-y-8 min-h-screen flex flex-col">
      {/* Header */}
      <div className="bg-slate-900 rounded-3xl p-8 text-white shadow-2xl flex flex-col md:flex-row items-center justify-between border-b-8 border-indigo-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>
        <div className="z-10 text-center md:text-left">
          <Badge className="bg-indigo-500 hover:bg-indigo-600 text-white mb-4">Esports / Tournament Mode</Badge>
          <h2 className="text-4xl md:text-5xl font-black mb-2 uppercase tracking-tighter">
            Ultimate Bracket <span className="text-indigo-400">Generator</span>
          </h2>
          <p className="text-slate-400 max-w-xl text-lg">
            Paste your teams, project this on the big screen, and click to advance winners. Feel the tension rise as the tournament progresses!
          </p>
        </div>
        <div className="hidden md:flex flex-col items-center justify-center opacity-50 z-10">
          <Swords className="w-24 h-24 text-indigo-500" />
        </div>
      </div>

      <AnimatePresence mode="wait">
        {gameState === 'setup' && (
          <motion.div
            key="setup"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="flex-1 flex items-center justify-center"
          >
            <Card className="p-8 md:p-12 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-2xl max-w-2xl w-full">
              <div className="flex items-center justify-center w-20 h-20 bg-indigo-100 dark:bg-indigo-900/50 rounded-full mb-6 mx-auto">
                <Users className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="font-black text-3xl mb-2 text-center text-slate-800 dark:text-white uppercase tracking-tight">Enter Your Competitors</h3>
              <p className="text-slate-500 mb-6 text-center text-lg">Paste a list of names, teams, or ideas (one per line). We'll auto-seed the bracket and add BYEs if needed.</p>
              
              {/* Quick Presets */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-500">Quick Presets:</span>
                {Object.keys(BRACKET_PRESETS).map(key => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setInputText(BRACKET_PRESETS[key].join('\n'))}
                    className="text-[10px] font-mono font-bold px-2.5 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded border border-slate-300 dark:border-slate-700 cursor-pointer"
                  >
                    {key}
                  </button>
                ))}
              </div>

              {/* Seeding Strategy Toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-100 dark:bg-slate-800/60 rounded-xl mb-4 border border-slate-200 dark:border-slate-700">
                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Seeding Strategy:</span>
                <div className="flex gap-1 bg-white dark:bg-slate-900 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => setSeedMode('seeded')}
                    className={cn(
                      "px-3 py-1 text-xs font-bold rounded flex items-center gap-1.5 transition-all cursor-pointer",
                      seedMode === 'seeded' ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
                    )}
                  >
                    <ListOrdered size={13} /> Seeded (1 vs 8)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSeedMode('random')}
                    className={cn(
                      "px-3 py-1 text-xs font-bold rounded flex items-center gap-1.5 transition-all cursor-pointer",
                      seedMode === 'random' ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
                    )}
                  >
                    <Shuffle size={13} /> Random Shuffle
                  </button>
                </div>
              </div>

              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-slate-500">Competitors List</span>
                {hasActiveNode && (
                  <button
                    type="button"
                    onClick={() => setIsRosterModalOpen(true)}
                    className="text-xs font-mono font-bold uppercase tracking-wider text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 flex items-center gap-1.5 cursor-pointer bg-indigo-50 dark:bg-indigo-950/40 px-3 py-1 rounded-md border border-indigo-200 dark:border-indigo-800"
                  >
                    <Users size={14} /> Import from {nodeName || 'Org'} Roster
                  </button>
                )}
              </div>

              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Team Alpha&#10;The Destroyers&#10;Sales Dept&#10;Marketing Team"
                className="w-full h-64 p-6 rounded-2xl border-2 border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none resize-none font-bold text-lg shadow-inner"
              />
              
              <Button 
                onClick={generateBracket}
                disabled={inputText.trim().length === 0}
                className="w-full h-20 mt-8 text-2xl font-black bg-indigo-600 hover:bg-indigo-700 text-white shadow-xl shadow-indigo-600/30 uppercase tracking-widest"
              >
                Generate Bracket
              </Button>
            </Card>
          </motion.div>
        )}

        {(gameState === 'playing' || gameState === 'finished') && (
          <motion.div
            key="playing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex-1 w-full bg-slate-950 rounded-3xl p-4 md:p-8 overflow-x-auto border border-slate-800 relative shadow-inner"
          >
            {/* Top Toolbar */}
            <div className="flex justify-between items-center mb-8 sticky left-0 print:hidden">
              <h3 className="text-2xl font-black text-white uppercase tracking-wider flex items-center">
                <Trophy className="w-6 h-6 mr-3 text-yellow-400" /> Live Bracket
              </h3>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => window.print()}
                  className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  <Printer className="w-4 h-4 mr-2" /> Print Bracket
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => setGameState('setup')}
                  className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Start New
                </Button>
              </div>
            </div>

            {/* Bracket Layout */}
            <div className="flex flex-row items-stretch min-w-max gap-8 md:gap-16 pb-12">
              {rounds.map((round, rIndex) => (
                <div key={rIndex} className="flex flex-col justify-around gap-4 w-64 md:w-80 shrink-0 relative">
                  
                  {/* Round Header */}
                  <div className="absolute -top-8 inset-x-0 text-center font-bold text-slate-500 uppercase tracking-widest text-sm">
                    {rIndex === rounds.length - 1 ? 'Finals' : 
                     rIndex === rounds.length - 2 ? 'Semifinals' : 
                     `Round ${rIndex + 1}`}
                  </div>

                  {round.map((match, mIndex) => (
                    <div key={match.id} className="relative py-2">
                      <Card className={cn(
                        "p-0 overflow-hidden border-2 shadow-lg transition-all duration-300 bg-slate-900",
                        match.winner ? "border-slate-700/50" : "border-indigo-500/50 shadow-indigo-500/20"
                      )}>
                        
                        {/* Team A */}
                        <button
                          onClick={() => match.teamA && match.teamA !== "BYE" && !match.winner && advanceTeam(rIndex, mIndex, match.teamA)}
                          disabled={!!match.winner || !match.teamA || match.teamA === "BYE"}
                          className={cn(
                            "w-full p-4 flex items-center justify-between text-left transition-colors relative group",
                            match.winner === match.teamA ? "bg-indigo-600 text-white" : 
                            match.winner ? "bg-slate-900 text-slate-600" :
                            match.teamA === "BYE" ? "bg-slate-900 text-slate-600" :
                            match.teamA ? "bg-slate-800 text-white hover:bg-slate-700 cursor-pointer" : "bg-slate-900 text-slate-700 h-14"
                          )}
                        >
                          <span className="font-bold text-lg truncate z-10">{match.teamA || "TBD"}</span>
                          {!match.winner && match.teamA && match.teamA !== "BYE" && (
                            <span className="opacity-0 group-hover:opacity-100 text-xs font-bold uppercase tracking-wider text-indigo-300 z-10 transition-opacity">Advance</span>
                          )}
                          {match.winner === match.teamA && (
                            <motion.div layoutId={`win-${match.teamA}`} className="absolute inset-0 bg-indigo-600 z-0"></motion.div>
                          )}
                        </button>

                        <div className="h-[2px] w-full bg-slate-950"></div>

                        {/* Team B */}
                        <button
                          onClick={() => match.teamB && match.teamB !== "BYE" && !match.winner && advanceTeam(rIndex, mIndex, match.teamB)}
                          disabled={!!match.winner || !match.teamB || match.teamB === "BYE"}
                          className={cn(
                            "w-full p-4 flex items-center justify-between text-left transition-colors relative group",
                            match.winner === match.teamB ? "bg-indigo-600 text-white" : 
                            match.winner ? "bg-slate-900 text-slate-600" :
                            match.teamB === "BYE" ? "bg-slate-900 text-slate-600" :
                            match.teamB ? "bg-slate-800 text-white hover:bg-slate-700 cursor-pointer" : "bg-slate-900 text-slate-700 h-14"
                          )}
                        >
                          <span className="font-bold text-lg truncate z-10">{match.teamB || "TBD"}</span>
                          {!match.winner && match.teamB && match.teamB !== "BYE" && (
                            <span className="opacity-0 group-hover:opacity-100 text-xs font-bold uppercase tracking-wider text-indigo-300 z-10 transition-opacity">Advance</span>
                          )}
                          {match.winner === match.teamB && (
                            <motion.div layoutId={`win-${match.teamB}`} className="absolute inset-0 bg-indigo-600 z-0"></motion.div>
                          )}
                        </button>
                      </Card>

                      {/* Connecting Lines (Drawing paths to next round) */}
                      {rIndex < rounds.length - 1 && (
                        <>
                          <div className="absolute top-1/2 -right-4 md:-right-8 w-4 md:w-8 h-[2px] bg-slate-700"></div>
                          <div className={cn(
                            "absolute w-[2px] bg-slate-700 -right-4 md:-right-8",
                            mIndex % 2 === 0 ? "top-1/2 h-[calc(50%+1rem)]" : "bottom-1/2 h-[calc(50%+1rem)]"
                          )}></div>
                        </>
                      )}
                      {/* Incoming line from previous round pair */}
                      {rIndex > 0 && (
                        <div className="absolute top-1/2 -left-4 md:-left-8 w-4 md:w-8 h-[2px] bg-slate-700"></div>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {/* Champion Column */}
              <div className="flex flex-col justify-center w-64 md:w-80 shrink-0 relative pl-8">
                 {/* Incoming line to Champion */}
                 <div className="absolute top-1/2 left-0 w-8 h-[2px] bg-slate-700"></div>

                 <Card className={cn(
                    "p-8 text-center border-4 transition-all duration-700",
                    champion ? "border-yellow-400 bg-gradient-to-b from-yellow-500/20 to-slate-900 shadow-[0_0_50px_rgba(250,204,21,0.3)] scale-110 z-20" : "border-slate-800 bg-slate-900"
                 )}>
                    <Crown className={cn(
                      "w-16 h-16 mx-auto mb-4 transition-colors duration-700",
                      champion ? "text-yellow-400 drop-shadow-[0_0_15px_rgba(250,204,21,0.8)]" : "text-slate-700"
                    )} />
                    <div className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-2">Grand Champion</div>
                    <div className={cn(
                      "font-black text-3xl break-words",
                      champion ? "text-white" : "text-slate-700"
                    )}>
                      {champion || "TBD"}
                    </div>
                 </Card>
              </div>
            </div>

            {/* Finished Overlay / Confetti simulation */}
            <AnimatePresence>
              {gameState === 'finished' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center bg-black/40 backdrop-blur-sm"
                >
                  <motion.div
                    initial={{ scale: 0.5, y: 50 }}
                    animate={{ scale: 1, y: 0 }}
                    transition={{ type: 'spring', bounce: 0.6 }}
                    className="bg-slate-900 p-12 rounded-[3rem] border-8 border-yellow-400 shadow-[0_0_100px_rgba(250,204,21,0.5)] text-center max-w-2xl pointer-events-auto"
                  >
                    <Trophy className="w-32 h-32 text-yellow-400 mx-auto mb-6 animate-bounce drop-shadow-[0_0_30px_rgba(250,204,21,0.8)]" />
                    <h2 className="text-6xl font-black text-white uppercase tracking-tighter mb-4 drop-shadow-md">
                      {champion}
                    </h2>
                    <p className="text-yellow-200 text-2xl font-bold uppercase tracking-widest mb-10">Tournament Champion!</p>
                    
                    <Button 
                      size="lg"
                      onClick={() => setGameState('setup')}
                      className="h-16 px-12 text-xl font-bold bg-white text-slate-900 hover:bg-slate-200 shadow-xl"
                    >
                      Create New Tournament
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {hasActiveNode && (
        <NodeRosterPickerModal
          isOpen={isRosterModalOpen}
          onClose={() => setIsRosterModalOpen(false)}
          onImport={handleImportFromNode}
          title={`Import ${nodeName || 'Organization'} Competitors`}
          description={`Select members from ${nodeName} to seed into the tournament bracket.`}
          confirmLabel="Add to Bracket"
        />
      )}

      <style type="text/css" media="print">
        {`
          @page {
            size: landscape;
            margin: 8mm;
          }
          body {
            background-color: #020617 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        `}
      </style>
    </div>
  );
}
